import { motion } from 'framer-motion';
import { ShoppingCart, Stethoscope, Landmark, Car, GraduationCap, Plane, Utensils, Home, Scale, Briefcase, Factory, Heart, Leaf, Music, Camera, Dumbbell, Palette, Wifi, Shield, Baby, Dog, Shirt, Gem, Wrench, Truck, Coffee, Scissors, BookOpen, Smartphone } from 'lucide-react';
import { useLang } from '../../../context/LangContext';

const sectors = [
  { id: 1, nameKey: "E-commerce", nameEn: "E-commerce", icon: ShoppingCart, color: "#54A133" },
  { id: 2, nameKey: "Santé", nameEn: "Healthcare", icon: Stethoscope, color: "#5399C2" },
  { id: 3, nameKey: "Banque & Finance", nameEn: "Banking & Finance", icon: Landmark, color: "#E0C568" },
  { id: 4, nameKey: "Automobile", nameEn: "Automotive", icon: Car, color: "#54A133" },
  { id: 5, nameKey: "Éducation", nameEn: "Education", icon: GraduationCap, color: "#5399C2" },
  { id: 6, nameKey: "Voyage & Tourisme", nameEn: "Travel & Tourism", icon: Plane, color: "#E0C568" },
  { id: 7, nameKey: "Restauration", nameEn: "Restaurant", icon: Utensils, color: "#54A133" },
  { id: 8, nameKey: "Immobilier", nameEn: "Real Estate", icon: Home, color: "#5399C2" },
  { id: 9, nameKey: "Juridique", nameEn: "Legal", icon: Scale, color: "#E0C568" },
  { id: 10, nameKey: "Consulting", nameEn: "Consulting", icon: Briefcase, color: "#54A133" },
  { id: 11, nameKey: "Industrie", nameEn: "Manufacturing", icon: Factory, color: "#5399C2" },
  { id: 12, nameKey: "Assurance", nameEn: "Insurance", icon: Shield, color: "#E0C568" },
  { id: 13, nameKey: "ONG & Associations", nameEn: "NGO & Nonprofits", icon: Heart, color: "#54A133" },
  { id: 14, nameKey: "Agriculture", nameEn: "Agriculture", icon: Leaf, color: "#5399C2" },
  { id: 15, nameKey: "Divertissement", nameEn: "Entertainment", icon: Music, color: "#E0C568" },
  { id: 16, nameKey: "Photographie", nameEn: "Photography", icon: Camera, color: "#54A133" },
  { id: 17, nameKey: "Fitness & Sport", nameEn: "Fitness & Sports", icon: Dumbbell, color: "#5399C2" },
  { id: 18, nameKey: "Art & Design", nameEn: "Art & Design", icon: Palette, color: "#E0C568" },
  { id: 19, nameKey: "Télécom & IT", nameEn: "Telecom & IT", icon: Wifi, color: "#54A133" },
  { id: 20, nameKey: "Sécurité", nameEn: "Security", icon: Shield, color: "#5399C2" },
  { id: 21, nameKey: "Petite Enfance", nameEn: "Childcare", icon: Baby, color: "#E0C568" },
  { id: 22, nameKey: "Animalerie", nameEn: "Pet Services", icon: Dog, color: "#54A133" },
  { id: 23, nameKey: "Mode & Textile", nameEn: "Fashion & Textile", icon: Shirt, color: "#5399C2" },
  { id: 24, nameKey: "Joaillerie & Luxe", nameEn: "Jewelry & Luxury", icon: Gem, color: "#E0C568" },
  { id: 25, nameKey: "Services Techniques", nameEn: "Technical Services", icon: Wrench, color: "#54A133" },
  { id: 26, nameKey: "Logistique", nameEn: "Logistics", icon: Truck, color: "#5399C2" },
  { id: 27, nameKey: "Hôtellerie", nameEn: "Hospitality", icon: Coffee, color: "#E0C568" },
  { id: 28, nameKey: "Beauté & Bien-être", nameEn: "Beauty & Wellness", icon: Scissors, color: "#54A133" },
  { id: 29, nameKey: "Édition & Média", nameEn: "Publishing & Media", icon: BookOpen, color: "#5399C2" },
  { id: 30, nameKey: "Tech & Startups", nameEn: "Tech & Startups", icon: Smartphone, color: "#E0C568" },
];

export const SectorsGrid = () => {
  const { lang } = useLang();

  return (
    <section data-testid="sectors-grid" className="py-24 bg-[#020209]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {sectors.map((sector, index) => {
            const Icon = sector.icon;
            const name = lang === 'en' ? sector.nameEn : sector.nameKey;
            return (
              <motion.div
                key={sector.id}
                data-testid={`sector-${sector.id}`}
                className="group relative p-5 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.15] hover:bg-white/[0.04] transition-all duration-300 cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.5) }}
                whileHover={{ y: -4 }}
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors duration-300"
                  style={{ backgroundColor: `${sector.color}15` }}
                >
                  <Icon size={20} style={{ color: sector.color }} />
                </div>
                <p className="text-sm font-medium text-neutral-300 group-hover:text-white transition-colors">
                  {name}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Sector count */}
        <motion.p
          className="text-center text-neutral-600 text-sm mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          {sectors.length} {lang === 'en' ? 'sectors available' : 'secteurs disponibles'}
        </motion.p>
      </div>
    </section>
  );
};
